/**
 * SWR hooks — 统一数据获取层
 *
 * 提供带自动去重、缓存、revalidate 的 React hooks，
 * 以及命令式 mutate helpers 供 zustand store 使用。
 */

import useSWR, { mutate } from "swr";
import { apiFetch } from "@/lib/api/client";
import type { MySkillItem } from "@/lib/skill/skill-api";
import type { MemoryItem } from "@/lib/api/assistant";
import {
  getAccountBalance,
  getBillOverview,
  getUsageDetail,
  getTokenUsage,
  getRequestCount,
  getRechargeSummary,
  getTransactionList,
  getQuotaSelfData,
  getModelData,
  getModelLog,
} from "@/lib/api/wallet";
import type {
  AccountBalanceResp,
  BillOverviewResp,
  UsageDetailResp,
  TokenUsageResp,
  RequestCountResp,
  RechargeSummaryResp,
  TransactionListResp,
  QuotaDataItem,
  NewapiQuotaDataResp,
  NewapiLogResp,
} from "@/lib/api/wallet";

// ── SWR Keys ──

export const SWR_KEY_MY_SKILLS = "/skill/api/v1/my/skills";
export const SWR_KEY_MEMORIES = "/api/agent/assistant/memories";
export const SWR_KEY_BALANCE = "/discourse/api/v1/wallet/balance";
export const SWR_KEY_BILL_OVERVIEW = "/discourse/api/v1/wallet/bill/overview";

export function swrKeyBillUsage(month: string) {
  return `/discourse/api/v1/wallet/bill/usage?month=${month}`;
}
export function swrKeyBillTokens(month: string) {
  return `/discourse/api/v1/wallet/bill/tokens?month=${month}`;
}
export function swrKeyBillRequests(month: string) {
  return `/discourse/api/v1/wallet/bill/requests?month=${month}`;
}

export function swrKeyQuotaSelfData(startTs: number, endTs: number) {
  return `/api/data/self?start_timestamp=${startTs}&end_timestamp=${endTs}`;
}

export function swrKeyModelData(
  startTs: number,
  endTs: number,
  defaultTime: "hour" | "day" | "week",
) {
  return `/discourse/api/v1/model/data?start_timestamp=${startTs}&end_timestamp=${endTs}&default_time=${defaultTime}`;
}

export function swrKeyModelLog(params: {
  p: number;
  pageSize: number;
  startTimestamp?: number;
  endTimestamp?: number;
}) {
  const sp = new URLSearchParams({
    p: String(params.p),
    page_size: String(params.pageSize),
  });
  if (params.startTimestamp)
    sp.set("start_timestamp", String(params.startTimestamp));
  if (params.endTimestamp) sp.set("end_timestamp", String(params.endTimestamp));
  return `/discourse/api/v1/model/log?${sp.toString()}`;
}

export const SWR_KEY_RECHARGE_SUMMARY =
  "/discourse/api/v1/wallet/recharge/summary";

export function swrKeyTransactions(params: {
  page: number;
  pageSize: number;
  tradeType?: number;
  startDate?: string;
  endDate?: string;
}) {
  const sp = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
    tradeType: String(params.tradeType ?? 0),
  });
  if (params.startDate) sp.set("startDate", params.startDate);
  if (params.endDate) sp.set("endDate", params.endDate);
  return `/discourse/api/v1/wallet/transactions?${sp.toString()}`;
}

// ── Fetchers ──

async function fetchSkills(): Promise<MySkillItem[]> {
  const resp = await apiFetch<{ items?: MySkillItem[] }>(SWR_KEY_MY_SKILLS);
  return resp.items ?? [];
}

async function fetchMemories(): Promise<MemoryItem[]> {
  return apiFetch<MemoryItem[]>(SWR_KEY_MEMORIES);
}

// ── React Hooks ──

export function useAccountBalance() {
  return useSWR<AccountBalanceResp>(
    SWR_KEY_BALANCE,
    () => getAccountBalance(),
    {
      dedupingInterval: 30_000,
      revalidateOnFocus: false,
    },
  );
}

export function useBillOverview() {
  return useSWR<BillOverviewResp>(
    SWR_KEY_BILL_OVERVIEW,
    () => getBillOverview(),
    {
      dedupingInterval: 30_000,
      revalidateOnFocus: false,
    },
  );
}

export function useUsageDetail(month: string) {
  return useSWR<UsageDetailResp>(
    swrKeyBillUsage(month),
    () => getUsageDetail(month),
    {
      dedupingInterval: 30_000,
      revalidateOnFocus: false,
    },
  );
}

export function useTokenUsage(month: string) {
  return useSWR<TokenUsageResp>(
    swrKeyBillTokens(month),
    () => getTokenUsage(month),
    {
      dedupingInterval: 30_000,
      revalidateOnFocus: false,
    },
  );
}

export function useRequestCount(month: string) {
  return useSWR<RequestCountResp>(
    swrKeyBillRequests(month),
    () => getRequestCount(month),
    {
      dedupingInterval: 30_000,
      revalidateOnFocus: false,
    },
  );
}

/**
 * 获取当前用户的模型调用量数据（/api/data/self）
 *
 * @param startTs 开始时间戳（秒）
 * @param endTs   结束时间戳（秒）
 */
export function useQuotaSelfData(startTs: number, endTs: number) {
  const key =
    startTs > 0 && endTs > 0 ? swrKeyQuotaSelfData(startTs, endTs) : null;
  return useSWR<QuotaDataItem[]>(
    key,
    () => getQuotaSelfData({ start_timestamp: startTs, end_timestamp: endTs }),
    {
      dedupingInterval: 30_000,
      revalidateOnFocus: false,
    },
  );
}

export function useRechargeSummary() {
  return useSWR<RechargeSummaryResp>(
    SWR_KEY_RECHARGE_SUMMARY,
    () => getRechargeSummary(),
    {
      dedupingInterval: 30_000,
      revalidateOnFocus: false,
    },
  );
}

export function useTransactionList(params: {
  page: number;
  pageSize: number;
  tradeType?: number;
  startDate?: string;
  endDate?: string;
}) {
  const key = swrKeyTransactions(params);
  return useSWR<TransactionListResp>(key, () => getTransactionList(params), {
    dedupingInterval: 10_000,
    revalidateOnFocus: false,
  });
}

/**
 * 透传 new-api 用量数据（/discourse/api/v1/model/data）
 *
 * @param startTs 开始时间戳（秒）
 * @param endTs   结束时间戳（秒）
 * @param defaultTime 时间粒度: hour/day/week
 */
export function useModelData(
  startTs: number,
  endTs: number,
  defaultTime: "hour" | "day" | "week" = "day",
) {
  const key =
    startTs > 0 && endTs > 0
      ? swrKeyModelData(startTs, endTs, defaultTime)
      : null;
  return useSWR<NewapiQuotaDataResp>(
    key,
    () =>
      getModelData({
        start_timestamp: startTs,
        end_timestamp: endTs,
        default_time: defaultTime,
      }),
    {
      dedupingInterval: 30_000,
      revalidateOnFocus: false,
    },
  );
}

/**
 * 透传 new-api 日志查询（/discourse/api/v1/model/log）
 *
 * 用于月度账单的详细日志列表。
 */
export function useModelLog(params: {
  p: number;
  pageSize: number;
  startTimestamp?: number;
  endTimestamp?: number;
}) {
  const key = swrKeyModelLog(params);
  return useSWR<NewapiLogResp>(
    key,
    () =>
      getModelLog({
        p: params.p,
        page_size: params.pageSize,
        start_timestamp: params.startTimestamp,
        end_timestamp: params.endTimestamp,
      }),
    {
      dedupingInterval: 10_000,
      revalidateOnFocus: false,
    },
  );
}

export function useMySkills() {
  return useSWR<MySkillItem[]>(SWR_KEY_MY_SKILLS, fetchSkills, {
    dedupingInterval: 60_000,
    revalidateOnFocus: false,
    fallbackData: [],
  });
}

export function useMySkillKeys() {
  const { data, ...rest } = useMySkills();
  const keys = data
    ? new Set(data.map((item) => item.skillKey).filter(Boolean))
    : new Set<string>();
  return { data: keys, ...rest };
}

export function useMemories() {
  return useSWR<MemoryItem[]>(SWR_KEY_MEMORIES, fetchMemories, {
    dedupingInterval: 60_000,
    revalidateOnFocus: false,
    fallbackData: [],
  });
}

// ── 命令式 Mutate Helpers（供 zustand store / 非 hook 场景使用） ──

export function invalidateSkillsCache() {
  return mutate(SWR_KEY_MY_SKILLS);
}

export function invalidateMemoriesCache() {
  return mutate(SWR_KEY_MEMORIES);
}

/**
 * 预加载 skills（触发 SWR 缓存填充，适合 store 中命令式调用）
 */
export function preloadMySkills(): Promise<MySkillItem[]> {
  return mutate(SWR_KEY_MY_SKILLS, fetchSkills(), {
    revalidate: false,
  }) as Promise<MySkillItem[]>;
}

/**
 * 预加载 memories
 */
export function preloadMemories(): Promise<MemoryItem[]> {
  return mutate(SWR_KEY_MEMORIES, fetchMemories(), {
    revalidate: false,
  }) as Promise<MemoryItem[]>;
}

export function invalidateBalanceCache() {
  return mutate(SWR_KEY_BALANCE);
}

export function preloadBalance(): Promise<AccountBalanceResp> {
  return mutate(SWR_KEY_BALANCE, getAccountBalance(), {
    revalidate: false,
  }) as Promise<AccountBalanceResp>;
}

export function invalidateBillOverviewCache() {
  return mutate(SWR_KEY_BILL_OVERVIEW);
}

export function preloadBillOverview(): Promise<BillOverviewResp> {
  return mutate(SWR_KEY_BILL_OVERVIEW, getBillOverview(), {
    revalidate: false,
  }) as Promise<BillOverviewResp>;
}

export function invalidateUsageCache(month: string) {
  return Promise.all([
    mutate(swrKeyBillUsage(month)),
    mutate(swrKeyBillTokens(month)),
    mutate(swrKeyBillRequests(month)),
  ]);
}

export function invalidateRechargeCache() {
  return Promise.all([
    mutate(SWR_KEY_RECHARGE_SUMMARY),
    mutate(
      (key) =>
        typeof key === "string" &&
        key.startsWith("/discourse/api/v1/wallet/transactions"),
    ),
  ]);
}
